// Gemeinsame Bausteine der Komposer: Warnhinweise mit Bestätigung, Vorlagenauswahl, Kopf.
import { useCallback, useEffect, useState, type ReactNode } from "react";
import { Icon, type IconName } from "../components/Icon";
import { komm } from "./api";
import type { Beleg, Vorlage, Warnung } from "./typen";
import "./kommunikation.css";

// --- Warnhinweise ----------------------------------------------------------------------------------------------------

interface WarnProps {
  warnungen: Warnung[];
  belege?: Beleg[];
  bestaetigt: string[];
  onBestaetigt: (codes: string[]) => void;
}

/** Hinweise vor dem Senden. "sperre" blockiert, "rot" und "gelb" verlangen ein Häkchen. */
export function Warnungen({ warnungen, belege, bestaetigt, onBestaetigt }: WarnProps) {
  const umschalten = (code: string, an: boolean) => onBestaetigt(an ? [...bestaetigt.filter((c) => c !== code), code] : bestaetigt.filter((c) => c !== code));
  return (
    <>
      {warnungen.map((w) => (
        <div key={w.code} className={`km-warnung km-warnung--${w.stufe}`} role={w.stufe === "gelb" ? "status" : "alert"}>
          <div className="km-warnung__kopf">
            <Icon name="warnung" groesse={15} />
            <strong>{w.titel}</strong>
          </div>
          <p className="km-warnung__text">{w.text}</p>
          {w.bestaetigung_noetig ? (
            <label className="km-warnung__haken">
              <input type="checkbox" checked={bestaetigt.includes(w.code)} onChange={(e) => umschalten(w.code, e.target.checked)} />
              <span>{w.bestaetigung_text ?? "Ich habe das gelesen und will trotzdem senden."}</span>
            </label>
          ) : null}
        </div>
      ))}
      {warnungen.length === 0 && belege && belege.length > 0 ? (
        <div className="km-beleg" title={`Bisheriger Kontakt: ${belege.map((b) => b.text).join(" · ")}`}>
          <Icon name="haken" groesse={13} />
          <span className="km-beleg__text">Bisheriger Kontakt: {belege.map((b) => b.text).join(" · ")}</span>
        </div>
      ) : null}
    </>
  );
}

/** Zusätzliche Hinweise aus der Antwort „bestaetigung_noetig“ zu den vorhandenen mischen (ohne Doppelte). */
export function warnungenMischen(vorhanden: Warnung[], neu: Warnung[]): Warnung[] {
  const codes = new Set(vorhanden.map((w) => w.code));
  return [...vorhanden, ...neu.filter((w) => !codes.has(w.code))];
}

export function sendenGesperrt(warnungen: Warnung[], bestaetigt: string[]): { gesperrt: boolean; offen: Warnung[] } {
  return {
    gesperrt: warnungen.some((w) => w.stufe === "sperre"),
    offen: warnungen.filter((w) => w.bestaetigung_noetig && !bestaetigt.includes(w.code)),
  };
}

// --- Vorlagen --------------------------------------------------------------------------------------------------------

export function useVorlagen(art: "email" | "sms") {
  const [liste, setListe] = useState<Vorlage[]>([]);
  const [laedt, setLaedt] = useState(true);
  const [fehler, setFehler] = useState<unknown>(null);

  const laden = useCallback(async () => {
    setLaedt(true);
    try {
      setListe((await komm.vorlagen(art)).data);
      setFehler(null);
    } catch (e) {
      setFehler(e);
    } finally {
      setLaedt(false);
    }
  }, [art]);

  useEffect(() => {
    void laden();
  }, [laden]);

  return { liste, laedt, fehler, neuLaden: laden };
}

interface VorlagenWahlProps {
  art: "email" | "sms";
  liste: Vorlage[];
  laedt: boolean;
  wert: string;
  onWahl: (id: string) => void;
  deaktiviert?: boolean;
}

export function VorlagenWahl({ liste, laedt, wert, onWahl, deaktiviert, art }: VorlagenWahlProps) {
  return (
    <select
      className="eingabe eingabe--klein km-vorlage"
      value={wert}
      disabled={deaktiviert || laedt || liste.length === 0}
      aria-label={art === "email" ? "E-Mail-Vorlage" : "SMS-Vorlage"}
      title="Die Vorlage füllt Betreff und Text mit den Daten dieses Leads. Danach lässt sich alles ändern."
      onChange={(e) => onWahl(e.target.value)}
    >
      <option value="">{laedt ? "Vorlagen laden …" : liste.length === 0 ? "Keine Vorlagen" : "Vorlage …"}</option>
      {liste.map((v) => (
        <option key={v.id} value={v.id}>
          {v.name}
        </option>
      ))}
    </select>
  );
}

// --- Kopf des Komposers ------------------------------------------------------------------------------------------------

interface KopfProps {
  icon: IconName;
  titel: string;
  hinweis?: ReactNode;
  /** z. B. "Entwurf gespeichert" */
  rechts?: ReactNode;
  onClose: () => void;
}

export function KomposerKopf({ icon, titel, hinweis, rechts, onClose }: KopfProps) {
  return (
    <div className="km-kopf">
      <span className={`km-symbol km-symbol--${icon}`} aria-hidden="true">
        <Icon name={icon} groesse={13} />
      </span>
      <span className="km-kopf__titel">{titel}</span>
      {hinweis ? <span className="km-kopf__hinweis">{hinweis}</span> : null}
      {rechts ? <span className="km-kopf__rechts">{rechts}</span> : null}
      <button type="button" className="symbolknopf symbolknopf--klein km-kopf__zu" title="Schließen (Esc), der Text bleibt als Entwurf" aria-label="Schließen" onClick={onClose}>
        <Icon name="schliessen" groesse={14} />
      </button>
    </div>
  );
}
